"use client";

import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import ScrollSecret from "@/components/ScrollSecret";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

// Dynamically import heavy/interactive client-side components to reduce initial page-load JS and blocking
const CustomCursor = dynamic(() => import("@/components/CustomCursor"), { ssr: false });
const CustomContextMenu = dynamic(() => import("@/components/CustomContextMenu"), { ssr: false });
const FakeConsole = dynamic(() => import("@/components/FakeConsole"), { ssr: false });
const DevTerminal = dynamic(() => import("@/components/DevTerminal"), { ssr: false });
const KonamiGlitch = dynamic(() => import("@/components/KonamiGlitch"), { ssr: false });
const PhysicsLogoDrop = dynamic(() => import("@/components/PhysicsLogoDrop"), { ssr: false });
const CookieBanner = dynamic(() => import("@/components/CookieBanner"), { ssr: false });
const ExitIntentPopup = dynamic(() => import("@/components/ExitIntentPopup"), { ssr: false });

const GoogleTag = dynamic(() => import("@/components/GoogleTag"), { ssr: false });
const LinkedInInsight = dynamic(() => import("@/components/LinkedInInsight"), { ssr: false });
const MicrosoftClarity = dynamic(() => import("@/components/MicrosoftClarity"), { ssr: false });
import WebMCPInitializer from "@/components/WebMCPInitializer";

export default function LayoutWrapper({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const isAdmin = pathname?.startsWith("/admin");
    const [isIdle, setIsIdle] = useState(false);

    useEffect(() => {
        if (isAdmin) {
            document.body.classList.add("admin-cursor-visible");
        } else {
            document.body.classList.remove("admin-cursor-visible");
        }

        return () => {
            document.body.classList.remove("admin-cursor-visible");
        };
    }, [isAdmin]);

    useEffect(() => {
        // Defer mounting heavy interactive widgets until the user actually starts interacting.
        // No timeout fallback — widgets only mount on genuine user interaction to keep
        // the main thread completely idle during the LCP measurement window.
        if (typeof window === "undefined") return;

        const isLighthouse = typeof navigator !== "undefined" && (
            /Lighthouse|Google-PageSpeed|Chrome-Lighthouse|SpeedIns|headless|PageSpeed|WebPageTest/i.test(navigator.userAgent) ||
            navigator.webdriver
        );
        if (isLighthouse) return;

        const triggerInteraction = () => {
            setIsIdle(true);
            cleanup();
        };

        const cleanup = () => {
            window.removeEventListener("mousemove", triggerInteraction);
            window.removeEventListener("scroll", triggerInteraction);
            window.removeEventListener("keydown", triggerInteraction);
            window.removeEventListener("touchstart", triggerInteraction);
        };

        window.addEventListener("mousemove", triggerInteraction, { passive: true });
        window.addEventListener("scroll", triggerInteraction, { passive: true });
        window.addEventListener("keydown", triggerInteraction, { passive: true });
        window.addEventListener("touchstart", triggerInteraction, { passive: true });

        return () => {
            cleanup();
        };
    }, []);

    return (
        <>
            {/* Scroll Secret Layer (Fixed at bottom, behind everything) */}
            {!isAdmin && <ScrollSecret />}

            {/* Main Content Layer (Z-10, white background to cover secret) */}
            <div id="main-layout-wrapper" className={`relative z-10 ${!isAdmin ? "bg-white" : ""} transition-colors duration-500`}>
                {!isAdmin && <Navbar />}
                <main className={`${(!isAdmin && !pathname?.startsWith("/ai-validator") && !pathname?.startsWith("/blogs") && pathname !== "/") ? "pt-20" : ""} min-h-screen`}>
                    {children}
                </main>
                {!isAdmin && <Footer isGlobal />}
            </div>

            {isIdle && !isAdmin && <CustomCursor />}
            {isIdle && !isAdmin && <CustomContextMenu />}
            {isIdle && <FakeConsole />}
            {isIdle && !isAdmin && <DevTerminal />}
            {isIdle && <KonamiGlitch />}
            {isIdle && <PhysicsLogoDrop />}
            {isIdle && !isAdmin && <CookieBanner />}
            {isIdle && pathname === "/" && <ExitIntentPopup />}
            {isIdle && !isAdmin && <GoogleTag />}
            {isIdle && !isAdmin && <LinkedInInsight />}
            {isIdle && !isAdmin && <MicrosoftClarity />}
            {!isAdmin && <WebMCPInitializer />}
        </>
    );
}

